import { ScheduleSettings } from '@mod/schedule';

import './index.d';
import './scheduleGraphQlExtension';
import {
  mapEmployeeOption,
  useAppointmentTypeTemplateOptionSource,
  usePatientOptionSource,
  usePatientProviderSearchOptionSource,
  useProviderSearchOptionSource,
  useUserContextSelector,
} from '@app/main/utils';
import { useMemo } from 'react';
import { openid, Status } from '@app/base';
import { Role } from '@app/main/api';

ScheduleSettings.useAuth = () => {
  const [id, name, avatar] = useUserContextSelector((x) => [x.id, x.name, x.avatar]);
  return useMemo(() => ({ id, name, avatar }), [id, name, avatar]);
};

ScheduleSettings.PRODUCT_KEY = import.meta.env.VITE_PRODUCT_CODE;

ScheduleSettings.useHostOptionSource = (args) => {
  const { visitorId, extra, oneOfIds } = args ?? {};

  const searchOptionSource = usePatientProviderSearchOptionSource({
    patientId: visitorId!,
    skip: !visitorId,
    active: true,
    oneOfIds,
  });

  const providerSearchOptionSource = useProviderSearchOptionSource({
    specialtyId: extra?.specialtyId,
    minimal: false,
    active: true,
    oneOfIds,
  });

  return visitorId ? searchOptionSource : providerSearchOptionSource;
};

ScheduleSettings.useVisitorOptionSource = (
  includeInactive?: boolean,
  careHomeId?: string,
  skipFetch?: boolean,
) => {
  const [roles, userCareHomeId] = useUserContextSelector((x) => [x.roles, x.careHomeId]);

  const isAdmin = roles.includes(Role.ADMIN);
  const isOnlyCareHomeStaff = roles.length === 1 && roles.includes(Role.CARE_HOME_STAFF);
  const effectiveCareHomeId = careHomeId ?? (isOnlyCareHomeStaff ? userCareHomeId : undefined);

  return usePatientOptionSource({
    statuses: isAdmin && includeInactive ? undefined : [Status.ACTIVE],
    careHomeId: effectiveCareHomeId ?? undefined,
    skip: skipFetch,
  });
};

ScheduleSettings.useHost = () => {
  const [isPerformer, id, email, firstName, lastName, avatar] = useUserContextSelector((x) => [
    x.hasRole('PERFORMER'),
    x.id,
    x.email,
    x.firstName,
    x.lastName,
    x.avatar,
  ]);

  return isPerformer
    ? mapEmployeeOption({ id, email, firstName, lastName, avatar, active: true })
    : undefined;
};

ScheduleSettings.useReferenceOptionSource = () => {
  return useAppointmentTypeTemplateOptionSource();
};

ScheduleSettings.isGuestMode = () => {
  const isAuthenticated = openid.isAuthenticated();
  return !isAuthenticated;
};

export { ScheduleTheme } from './ScheduleTheme';
